import React from "react";
import { Link } from "react-router-dom";
import { Col, Form, FormFeedback, Input, Label, Row } from "reactstrap";
import { useFormik } from "formik";
import changePasswordSchema from "./schemas/changePassword.schema";
import { toast } from "react-toastify";
import type { ChangePasswordFormValues } from "./types/settings.types";
import { changeOwnPassword } from "features/Users/services/UserService";

function messageFromChangePasswordError(err: unknown): string {
  const ax = err as {
    response?: {
      data?: {
        message?: string;
        errors?: Record<string, string[] | string> | unknown[];
      };
    };
  };
  const d = ax.response?.data;
  if (!d) return "Failed to change password";
  if (typeof d.message === "string" && d.message.trim()) return d.message;
  const errors = d.errors;
  if (errors && typeof errors === "object" && !Array.isArray(errors)) {
    const first = Object.values(errors as Record<string, unknown>)[0];
    if (Array.isArray(first) && first[0] != null)
      return String(first[0]);
    if (typeof first === "string") return first;
  }
  return "Failed to change password";
}

const ChangePasswordTab = () => {
  const formik = useFormik<ChangePasswordFormValues>({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: changePasswordSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      try {
        const response = await changeOwnPassword({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        });
        if (response.success) {
          toast.success(
            (typeof response.message === "string" && response.message) ||
              "Password changed successfully",
          );
          resetForm();
        } else {
          toast.error(response.message || "Failed to change password");
        }
      } catch (err: unknown) {
        toast.error(messageFromChangePasswordError(err));
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Row>
        <Col lg={12}>
          <Row className="mb-3">
            <Col lg={3}>
              <Label htmlFor="currentPassword" className="form-label">
                Current password <span className="text-danger">*</span>
              </Label>
            </Col>
            <Col lg={9}>
              <Input
                type="password"
                className="form-control"
                id="currentPassword"
                name="currentPassword"
                autoComplete="current-password"
                placeholder="Enter current password"
                value={formik.values.currentPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  !!(
                    formik.touched.currentPassword &&
                    formik.errors.currentPassword
                  )
                }
              />
              {formik.touched.currentPassword &&
                formik.errors.currentPassword && (
                  <FormFeedback type="invalid">
                    {formik.errors.currentPassword}
                  </FormFeedback>
                )}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col lg={3}>
              <Label htmlFor="newPassword" className="form-label">
                New password <span className="text-danger">*</span>
              </Label>
            </Col>
            <Col lg={9}>
              <Input
                type="password"
                className="form-control"
                id="newPassword"
                name="newPassword"
                autoComplete="new-password"
                placeholder="Enter new password (min. 6 characters)"
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  !!(formik.touched.newPassword && formik.errors.newPassword)
                }
              />
              {formik.touched.newPassword && formik.errors.newPassword && (
                <FormFeedback type="invalid">
                  {formik.errors.newPassword}
                </FormFeedback>
              )}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col lg={3}>
              <Label htmlFor="confirmPassword" className="form-label">
                Confirm new password <span className="text-danger">*</span>
              </Label>
            </Col>
            <Col lg={9}>
              <Input
                type="password"
                className="form-control"
                id="confirmPassword"
                name="confirmPassword"
                autoComplete="new-password"
                placeholder="Confirm new password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  !!(
                    formik.touched.confirmPassword &&
                    formik.errors.confirmPassword
                  )
                }
              />
              {formik.touched.confirmPassword &&
                formik.errors.confirmPassword && (
                  <FormFeedback type="invalid">
                    {formik.errors.confirmPassword}
                  </FormFeedback>
                )}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col lg={3} />
            <Col lg={9}>
              <Link
                to="/forgot-password"
                className="link-primary text-decoration-underline"
              >
                Forgot password?
              </Link>
            </Col>
          </Row>
        </Col>
        <Col lg={12}>
          <div className="hstack gap-2 justify-content-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  />
                  Changing…
                </>
              ) : (
                "Change password"
              )}
            </button>
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default ChangePasswordTab;
